import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';

const HTML2CANVAS_OPTS = {
  scale: 2,
  backgroundColor: '#ffffff',
  logging: false,
  useCORS: true,
};

function mergeCanvasesVertical(canvasTop, canvasBottom, gapPx = 16) {
  const w = Math.max(canvasTop.width, canvasBottom.width);
  const h = canvasTop.height + gapPx + canvasBottom.height;
  const merged = document.createElement('canvas');
  merged.width = w;
  merged.height = h;
  const ctx = merged.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(canvasTop, 0, 0);
  ctx.drawImage(canvasBottom, 0, canvasTop.height + gapPx);
  return merged;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Nome seguro para arquivo */
export function sanitizarNomeArquivo(nome) {
  const s = (nome || 'export').replace(/[<>:"/\\|?*]+/g, '_').replace(/\s+/g, '_');
  return s.slice(0, 80) || 'export';
}

/**
 * Captura KPI + bloco legenda/gráfico e exporta PNG.
 * @param {HTMLElement|null} kpiEl
 * @param {HTMLElement} graficoEl — legenda + canvas
 */
export async function exportarCargoPresencaPNG(kpiEl, graficoEl, baseFilename) {
  const c2 = await html2canvas(graficoEl, HTML2CANVAS_OPTS);
  const c1 = kpiEl ? await html2canvas(kpiEl, HTML2CANVAS_OPTS) : null;
  const merged = c1 ? mergeCanvasesVertical(c1, c2) : c2;
  downloadDataUrl(merged.toDataURL('image/png'), `${baseFilename}.png`);
}

export async function exportarCargoPresencaJPEG(kpiEl, graficoEl, baseFilename, quality = 0.92) {
  const c2 = await html2canvas(graficoEl, HTML2CANVAS_OPTS);
  const c1 = kpiEl ? await html2canvas(kpiEl, HTML2CANVAS_OPTS) : null;
  const merged = c1 ? mergeCanvasesVertical(c1, c2) : c2;
  downloadDataUrl(merged.toDataURL('image/jpeg', quality), `${baseFilename}.jpg`);
}

/**
 * PDF com imagem (KPI + gráfico) e tabela de colaboradores.
 * @param {{ tituloCargo: string, periodoLabel: string, tabelaLinhas: string[][] }} meta
 */
export async function exportarCargoPresencaPDF(kpiEl, graficoEl, baseFilename, meta) {
  const { tituloCargo, periodoLabel, tabelaLinhas } = meta;
  const c2 = await html2canvas(graficoEl, HTML2CANVAS_OPTS);
  const c1 = kpiEl ? await html2canvas(kpiEl, HTML2CANVAS_OPTS) : null;
  const merged = c1 ? mergeCanvasesVertical(c1, c2) : c2;
  const imgData = merged.toDataURL('image/png');

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let y = margin;

  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(24, 24, 27);
  pdf.text(tituloCargo || 'Frequência de presenças', margin, y);
  y += 7;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(82, 82, 91);
  pdf.text(periodoLabel || '', margin, y);
  y += 10;

  const maxW = pageW - 2 * margin;
  const imgProps = pdf.getImageProperties(imgData);
  let imgW = maxW;
  let imgH = (imgProps.height * imgW) / imgProps.width;
  const maxImgH = pageH - margin - y - 12;
  if (imgH > maxImgH) {
    imgH = maxImgH;
    imgW = (imgProps.width * imgH) / imgProps.height;
  }
  pdf.addImage(imgData, 'PNG', margin, y, imgW, imgH);
  y += imgH + 8;

  if (tabelaLinhas && tabelaLinhas.length > 0) {
    if (y > pageH - 30) {
      pdf.addPage();
      y = margin;
    }
    autoTable(pdf, {
      head: [['Colaborador', 'Reuniões', 'Presentes', 'Ausentes', 'Frequência']],
      body: tabelaLinhas,
      startY: y,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [52, 52, 58], textColor: [250, 250, 250] },
      alternateRowStyles: { fillColor: [250, 250, 250] },
    });
  }

  const rodape = `Gerado em ${new Date().toLocaleString('pt-BR')}`;
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(161, 161, 170);
    pdf.text(rodape, pageW / 2, pageH - 6, { align: 'center' });
  }

  pdf.save(`${baseFilename}.pdf`);
}

/** Captura um nó DOM (gráfico com títulos, tabela, etc.) como PNG. */
export async function exportarElementoComoPNG(element, baseFilename) {
  if (!element) throw new Error('Elemento inválido');
  const canvas = await html2canvas(element, HTML2CANVAS_OPTS);
  downloadDataUrl(canvas.toDataURL('image/png'), `${baseFilename}.png`);
}

/** Captura um nó DOM como JPEG. */
export async function exportarElementoComoJPEG(element, baseFilename, quality = 0.92) {
  if (!element) throw new Error('Elemento inválido');
  const canvas = await html2canvas(element, HTML2CANVAS_OPTS);
  downloadDataUrl(canvas.toDataURL('image/jpeg', quality), `${baseFilename}.jpg`);
}

/** Uma página PDF com a imagem capturada (ex.: só gráfico com títulos). */
export async function exportarElementoComoPDFImagem(element, baseFilename) {
  if (!element) throw new Error('Elemento inválido');
  const canvas = await html2canvas(element, HTML2CANVAS_OPTS);
  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const maxW = pageW - 2 * margin;
  const imgProps = pdf.getImageProperties(imgData);
  let imgW = maxW;
  let imgH = (imgProps.height * imgW) / imgProps.width;
  const maxH = pageH - 2 * margin;
  if (imgH > maxH) {
    imgH = maxH;
    imgW = (imgProps.width * imgH) / imgProps.height;
  }
  pdf.addImage(imgData, 'PNG', margin, margin, imgW, imgH);
  pdf.setFontSize(8);
  pdf.setTextColor(161, 161, 170);
  pdf.text(`Gerado em ${new Date().toLocaleString('pt-BR')}`, pageW / 2, pageH - 6, { align: 'center' });
  pdf.save(`${baseFilename}.pdf`);
}

/**
 * PDF apenas com tabela (dados em texto, não screenshot).
 * @param {string[][]} tabelaLinhas
 */
export function exportarCargoPresencaTabelaPDF(tabelaLinhas, baseFilename, meta) {
  const { tituloCargo, periodoLabel } = meta;
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let y = margin;

  pdf.setFontSize(14);
  pdf.setFont(undefined, 'bold');
  pdf.setTextColor(24, 24, 27);
  pdf.text(tituloCargo || 'Frequência de presenças', margin, y);
  y += 7;
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');
  pdf.setTextColor(82, 82, 91);
  pdf.text(periodoLabel || '', margin, y);
  y += 9;
  pdf.setFontSize(11);
  pdf.setFont(undefined, 'bold');
  pdf.text('Detalhamento por colaborador', margin, y);
  y += 6;

  autoTable(pdf, {
    head: [['Colaborador', 'Reuniões', 'Presentes', 'Ausentes', 'Frequência']],
    body: tabelaLinhas,
    startY: y,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [52, 52, 58], textColor: [250, 250, 250] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });

  const rodape = `Gerado em ${new Date().toLocaleString('pt-BR')}`;
  const pageCount = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setTextColor(161, 161, 170);
    pdf.text(rodape, pageW / 2, pageH - 6, { align: 'center' });
  }

  pdf.save(`${baseFilename}.pdf`);
}

// Normaliza campo do membro (API pode retornar snake_case ou camelCase)
const m = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj[k] ?? obj[k.replace(/_([a-z])/g, (_, l) => l.toUpperCase())];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

/** Nome do responsável (objeto ou string) */
const responsavelNome = (obj) => {
  const r = m(obj, 'responsavel');
  if (!r) return '—';
  if (typeof r === 'string') return r;
  return (r.nome_completo ?? r.nomeCompleto ?? '').toString() || '—';
};

const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  try {
    const hoje = new Date();
    const nasc = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nasc.getFullYear();
    const diffMonth = hoje.getMonth() - nasc.getMonth();
    if (diffMonth < 0 || (diffMonth === 0 && hoje.getDate() < nasc.getDate())) idade--;
    return idade;
  } catch (_) { return null; }
};

export function formatarCategoria(categoria) {
  switch (categoria) {
    case 'crianca': return 'Criança';
    case 'jovem': return 'Jovem';
    case 'adulto': return 'Adulto';
    default: return categoria || '—';
  }
}

const formatarTriState = (valor) => {
  if (valor === true) return 'Sim';
  if (valor === false) return 'Não';
  return 'Não informado';
};

const formatarAtualizacaoFrequencia = (valor) => {
  if (!valor) return '—';
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleString('pt-BR');
};

/**
 * Exporta dados da irmandade para PDF com layout de documento
 */
export const exportarPDF = (dados, titulo = 'Lista de Membros') => {
  try {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 14;
    let y = 20;

    // Cabeçalho do documento
    doc.setFillColor(248, 250, 252);
    doc.rect(0, 0, pageW, 36, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(0, 36, pageW, 36);

    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(titulo, margin, 16);

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })} — ${dados.length} registro(s)`, margin, 26);

    y = 44;

    const colunas = [
      'Nome', 'Categoria', 'Tipo', 'Idade', 'Telefone', 'Congregação', 'Endereço', 'Responsável',
      'É frequente?', 'Comparece à reunião?', 'Batizado?', 'Visita indicada?', 'Aceitou visita?',
      'Atualização da frequência'
    ];
    const linhas = dados.map(membro => {
      const idade = m(membro, 'idade') ?? calcularIdade(m(membro, 'data_nascimento', 'dataNascimento'));
      return [
        (m(membro, 'nome_completo', 'nomeCompleto') || '—').toString(),
        formatarCategoria(m(membro, 'categoria')),
        (m(membro, 'tipo_pessoa', 'tipoPessoa') === 'irmao' ? 'Irmão' : 'Irmã'),
        idade != null ? `${idade} anos` : '—',
        (m(membro, 'telefone') || '—').toString(),
        (m(membro, 'congregacao', 'congregacao') || '—').toString(),
        (m(membro, 'endereco', 'endereco') || '—').toString(),
        responsavelNome(membro),
        formatarTriState(m(membro, 'frequente_reuniao', 'frequenteReuniao')),
        formatarTriState(m(membro, 'comparece_reuniao', 'compareceReuniao')),
        m(membro, 'categoria') === 'jovem'
          ? formatarTriState(m(membro, 'batizado'))
          : 'Não se aplica',
        formatarTriState(m(membro, 'visita_indicada', 'visitaIndicada')),
        formatarTriState(m(membro, 'aceitou_visita', 'aceitouVisita')),
        formatarAtualizacaoFrequencia(m(membro, 'frequencia_atualizada_em', 'frequenciaAtualizadaEm'))
      ];
    });

    autoTable(doc, {
      head: [colunas],
      body: linhas,
      startY: y,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 5.8,
        cellPadding: 1.5,
        textColor: [51, 65, 85],
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [51, 65, 85],
        textColor: [248, 250, 252],
        fontStyle: 'bold',
        fontSize: 5.8,
        overflow: 'linebreak'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 27, overflow: 'linebreak' },
        1: { cellWidth: 13, overflow: 'linebreak' },
        2: { cellWidth: 10, overflow: 'linebreak' },
        3: { cellWidth: 10, overflow: 'linebreak' },
        4: { cellWidth: 20, overflow: 'linebreak' },
        5: { cellWidth: 18, overflow: 'linebreak' },
        6: { cellWidth: 29, overflow: 'linebreak' },
        7: { cellWidth: 24, overflow: 'linebreak' },
        8: { cellWidth: 15, overflow: 'linebreak' },
        9: { cellWidth: 17, overflow: 'linebreak' },
        10: { cellWidth: 14, overflow: 'linebreak' },
        11: { cellWidth: 16, overflow: 'linebreak' },
        12: { cellWidth: 16, overflow: 'linebreak' },
        13: { cellWidth: 22, overflow: 'linebreak' }
      }
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Página ${i} de ${pageCount}`,
        pageW / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    doc.save(`${titulo.replace(/\s+/g, '_')}_${new Date().getTime()}.pdf`);
    return true;
  } catch (error) {
    console.error('Erro ao exportar PDF:', error);
    throw new Error('Falha ao gerar arquivo PDF');
  }
};

/**
 * Exporta dados da irmandade para Excel com modelo de documento
 */
export const exportarExcel = (dados, nomeArquivo = 'Lista_Membros') => {
  try {
    const dadosExcel = dados.map(membro => ({
      'Nome Completo': (m(membro, 'nome_completo', 'nomeCompleto') || '—').toString(),
      'Categoria': formatarCategoria(m(membro, 'categoria')),
      'Tipo': (m(membro, 'tipo_pessoa', 'tipoPessoa') === 'irmao' ? 'Irmão' : 'Irmã'),
      'Idade': m(membro, 'idade') ?? calcularIdade(m(membro, 'data_nascimento', 'dataNascimento')) ?? '—',
      'Data Nascimento': (() => {
        const dt = m(membro, 'data_nascimento', 'dataNascimento');
        if (!dt) return '—';
        try {
          return new Date(dt).toLocaleDateString('pt-BR');
        } catch (_) { return '—'; }
      })(),
      'Telefone': (m(membro, 'telefone') || '—').toString(),
      'E-mail': (m(membro, 'email') || '—').toString(),
      'Congregação': (m(membro, 'congregacao', 'congregacao') || '—').toString(),
      'Endereço': (m(membro, 'endereco', 'endereco') || '—').toString(),
      'Responsável': responsavelNome(membro),
      'Cargos': (m(membro, 'cargos_nomes', 'cargosNomes') || '—').toString(),
      'É frequente?': formatarTriState(m(membro, 'frequente_reuniao', 'frequenteReuniao')),
      'Comparece à reunião?': formatarTriState(m(membro, 'comparece_reuniao', 'compareceReuniao')),
      'Batizado?': m(membro, 'categoria') === 'jovem'
        ? formatarTriState(m(membro, 'batizado'))
        : 'Não se aplica',
      'Visita indicada?': formatarTriState(m(membro, 'visita_indicada', 'visitaIndicada')),
      'Aceitou visita?': formatarTriState(m(membro, 'aceitou_visita', 'aceitouVisita')),
      'Atualização da frequência': formatarAtualizacaoFrequencia(m(membro, 'frequencia_atualizada_em', 'frequenciaAtualizadaEm'))
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dadosExcel);

    const colWidths = [
      { wch: 32 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 14 },
      { wch: 16 },
      { wch: 28 },
      { wch: 22 },
      { wch: 35 },
      { wch: 28 },
      { wch: 28 },
      { wch: 16 },
      { wch: 23 },
      { wch: 16 },
      { wch: 19 },
      { wch: 18 },
      { wch: 23 }
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Membros');
    XLSX.writeFile(wb, `${nomeArquivo}_${new Date().getTime()}.xlsx`);
    return true;
  } catch (error) {
    console.error('Erro ao exportar Excel:', error);
    throw new Error('Falha ao gerar arquivo Excel');
  }
};

/**
 * Exporta lista de aniversariantes para PDF
 */
export const exportarAniversariantesPDF = (dados, mes) => {
  const mesesPorExtenso = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  const mesNome = mes ? mesesPorExtenso[parseInt(mes) - 1] : 'Todos os Meses';
  return exportarPDF(dados, `Aniversariantes_${mesNome}`);
};

/**
 * Exporta lista de aniversariantes para Excel
 */
export const exportarAniversariantesExcel = (dados, mes) => {
  const mesesPorExtenso = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  
  const mesNome = mes ? mesesPorExtenso[parseInt(mes) - 1] : 'Todos';
  return exportarExcel(dados, `Aniversariantes_${mesNome}`);
};

// ─── Export WhatsApp ──────────────────────────────────────────────────────────

function formatarTelefoneWA(telefone) {
  const digits = (telefone || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('55') && digits.length >= 12) return digits;
  return '55' + digits;
}

function calcIdadeFromMembro(m) {
  const id = m.idade ?? m._idade ?? m.age;
  if (id != null) return Number(id);
  const dn = m.data_nascimento || m.dataNascimento;
  if (!dn) return null;
  const hoje = new Date();
  const nasc = new Date(dn);
  let a = hoje.getFullYear() - nasc.getFullYear();
  if (
    hoje.getMonth() < nasc.getMonth() ||
    (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())
  ) a--;
  return a;
}

/**
 * Tenta parsear "Livro Capítulo:Verso" em partes.
 * Retorna { prefix: "Livro Capítulo", startVerse: number } ou null.
 */
function parsearRecitativo(recitativo) {
  const match = /^(.+):(\d+)$/.exec((recitativo || '').trim());
  if (!match) return null;
  return { prefix: match[1].trim(), startVerse: parseInt(match[2], 10) };
}

/**
 * Tenta extrair { livro, capitulo } de uma string "Romanos 1" ou "1 Coríntios 5".
 */
function parsearLivroCap(prefix) {
  const match = /^(.*?)(?:\s+)(\d+)$/.exec((prefix || '').trim());
  if (!match) return { livro: prefix || '', capitulo: null };
  return { livro: match[1].trim(), capitulo: parseInt(match[2], 10) };
}

/**
 * Gera e faz download de um arquivo HTML com links wa.me clicáveis.
 * Abre automaticamente o WhatsApp com mensagem pré-preenchida ao clicar no telefone.
 *
 * - Jovens (>=12): versículo sequencial a cada membro; gênero pelo campo tipo_pessoa.
 * - Crianças (0-11): todos recebem o mesmo versículo (o digitado no modal).
 *
 * @param {object[]} membros
 * @param {object} opcoes
 * @param {string} opcoes.nomeAuxiliar
 * @param {string} opcoes.localidade
 * @param {string} opcoes.recitativo - "Livro Capítulo:Verso"
 * @param {string} [opcoes.titulo]
 * @param {Array<{ capitulo: number, verso: number }>} [opcoes.sequencia]
 *   Se fornecido, usa essa sequência (já calculada com carry-over).
 *   Cada índice corresponde ao membro de mesmo índice (apenas para jovens; crianças
 *   ignoram a sequência e usam o verso inicial).
 * @param {boolean} [opcoes.crianca1aTurmaMesmoVerso=true]
 *   Se true (padrão), 1a/2a turmas (crianças, idade <12) recebem todos o mesmo verso.
 */
export function exportarContatosWhatsAppHTML(membros, opcoes) {
  const {
    nomeAuxiliar,
    localidade,
    recitativo,
    titulo = 'Contatos WhatsApp',
    sequencia = null,
    crianca1aTurmaMesmoVerso = true,
  } = opcoes;

  const recitativoParsed = parsearRecitativo(recitativo);
  const livroCap = recitativoParsed ? parsearLivroCap(recitativoParsed.prefix) : { livro: '', capitulo: null };

  // Resolve o nome do responsável: API retorna como objeto aninhado { nome_completo }
  const nomeResponsavelCompleto = (m) =>
    m.responsavel?.nome_completo ||
    m.responsavel?.nomeCompleto ||
    m.responsavel_nome ||
    m.responsavelNome ||
    '';

  const gerarMsgCrianca = (membro, recitivoDoMembro) => {
    const nomeRespCompleto = nomeResponsavelCompleto(membro);
    const nomeResp = nomeRespCompleto.split(' ')[0] || 'responsável';
    const nomeCrianca = (membro.nome_completo || membro.nomeCompleto || '').split(' ')[0];
    return (
      `A paz de Deus irmão(a) ${nomeResp}, tudo bem?\n\n` +
      `Meu nome é ${nomeAuxiliar}, auxiliar de jovens e menores e estarei enviando o recitativo ` +
      `do(a) ${nomeCrianca} para recitar no próximo domingo na reunião de jovens na ${localidade}.\n\n` +
      `O recitativo vai ser *${recitivoDoMembro}*\n\n` +
      `Deus abençoe e contamos com a vossa presença!`
    );
  };

  const gerarMsgJovem = (membro, recitivoDoMembro) => {
    const primeiroNome = (membro.nome_completo || membro.nomeCompleto || '').split(' ')[0];
    const tipo = membro.tipo_pessoa ?? membro.tipoPessoa;
    const tratamento = tipo === 'irma' ? 'irmã' : 'irmão';
    return (
      `A paz de Deus ${tratamento} ${primeiroNome}, tudo bem?\n\n` +
      `Estou enviando o seu recitativo para o próximo domingo na reunião de jovens na ${localidade}.\n\n` +
      `O recitativo vai ser *${recitivoDoMembro}*\n\n` +
      `Deus abençoe e contamos com a vossa presença!`
    );
  };

  // Contador separado para jovens (versículo sequencial - fallback simples)
  let jovemIndex = 0;

  // Helper para formatar a referência a partir de capítulo/verso usando o livro do recitativo
  const fmtRef = (capitulo, verso) => {
    if (livroCap.livro) return `${livroCap.livro} ${capitulo}:${verso}`;
    return `${recitativoParsed?.prefix || ''}:${verso}`.replace(/^:/, '');
  };

  const rows = membros
    .map((m, idx) => {
      const nome = m.nome_completo || m.nomeCompleto || '—';
      const tel = m.telefone || '';
      const telWA = formatarTelefoneWA(tel);
      const idade = calcIdadeFromMembro(m);
      const idadeStr = idade != null ? `${idade} anos` : '—';
      const responsavelNome = nomeResponsavelCompleto(m);

      // <=11 → mensagem para o responsável; >=12 → mensagem direta
      const ehJovem = idade != null && idade >= 12;

      // Versículo final do membro
      let recitivoMembro = recitativo;
      if (sequencia && Array.isArray(sequencia) && sequencia[idx]) {
        // Se uma sequência foi fornecida (já com carry-over):
        // - Crianças (1a/2a turma): se "crianca1aTurmaMesmoVerso" estiver ativo, usam o primeiro item
        // - Jovens (3a turma): usam o item da sua posição
        const ref = (!ehJovem && crianca1aTurmaMesmoVerso) ? sequencia[0] : sequencia[idx];
        if (ref) recitivoMembro = fmtRef(ref.capitulo, ref.verso);
      } else if (ehJovem && recitativoParsed) {
        // Fallback antigo: incrementa só o verso, sem carry-over
        recitivoMembro = `${recitativoParsed.prefix}:${recitativoParsed.startVerse + jovemIndex}`;
        jovemIndex++;
      }

      const mensagem = ehJovem ? gerarMsgJovem(m, recitivoMembro) : gerarMsgCrianca(m, recitivoMembro);
      const turmaLabel = ehJovem ? '3ª turma' : '1ª/2ª turma';

      const telDisplay = tel || '—';
      const linkWA = telWA
        ? `<a href="https://wa.me/${telWA}?text=${encodeURIComponent(mensagem)}" class="wa-link" target="_blank" rel="noopener">📱 ${telDisplay}</a>`
        : `<span class="sem-tel">${telDisplay}</span>`;

      const tipo = (m.tipo_pessoa ?? m.tipoPessoa ?? '');
      const escNome = nome.replace(/"/g, '&quot;');
      const escResp = responsavelNome.replace(/"/g, '&quot;');

      // capítulo/verso individuais para uso no script embarcado (drag-and-drop)
      const refMembro = (sequencia && Array.isArray(sequencia) && sequencia[idx])
        ? sequencia[idx]
        : null;
      const dataCap = refMembro ? refMembro.capitulo : (livroCap.capitulo || '');
      const dataVerso = refMembro ? refMembro.verso : (recitativoParsed ? recitativoParsed.startVerse : '');

      // ehJovem OU crianca1aTurmaMesmoVerso=false → verso sequencial por pessoa
      const ehSequencial = ehJovem || !crianca1aTurmaMesmoVerso;
      return `<tr draggable="true"
      data-jovem="${ehJovem ? 1 : 0}"
      data-sequential="${ehSequencial ? 1 : 0}"
      data-nome="${escNome}"
      data-genero="${tipo}"
      data-tel="${telDisplay}"
      data-telwa="${telWA || ''}"
      data-resp="${escResp}"
      data-cap="${dataCap}"
      data-verso="${dataVerso}">
      <td class="drag-handle" title="Arraste para mover">⠿</td>
      <td class="num-cell" style="color:#94a3b8;font-size:.8rem;min-width:28px"></td>
      <td>${nome}</td>
      <td>${idadeStr}</td>
      <td><span class="turma-badge ${ehJovem ? 'jovem' : 'crianca'}">${turmaLabel}</span></td>
      <td class="recitativo-cell">${recitivoMembro}</td>
      <td class="tel-cell">${linkWA}</td>
      <td>${ehJovem ? '—' : (responsavelNome || '—')}</td>
    </tr>`;
    })
    .join('\n');

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${titulo}</title>
<style>
*{box-sizing:border-box}
body{font-family:system-ui,-apple-system,sans-serif;margin:0;padding:20px;background:#f0fdf4;color:#1e293b}
h1{font-size:1.4rem;margin:0 0 6px}
/* ── Painel de controle ── */
.ctrl{background:#fff;border:1px solid #d1fae5;border-radius:12px;padding:16px 20px;margin-bottom:18px;display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end}
.ctrl-group{display:flex;flex-direction:column;gap:4px;font-size:.82rem;font-weight:600;color:#166534}
.ctrl-group input{padding:6px 10px;border:1px solid #a7f3d0;border-radius:6px;font-size:.88rem;width:160px;outline:none}
.ctrl-group input:focus{border-color:#16a34a;box-shadow:0 0 0 2px #bbf7d0}
.btn-buscar-biblia{padding:6px 14px;background:#16a34a;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:.84rem;font-weight:600;align-self:flex-end}
.btn-buscar-biblia:hover{background:#15803d}
.biblia-resultado{font-size:.8rem;color:#475569;margin-top:4px;max-width:320px;line-height:1.4}
/* ── Aviso ── */
.aviso{background:#fefce8;border:1px solid #fde047;border-radius:8px;padding:10px 16px;font-size:.84rem;margin-bottom:18px}
/* ── Tabela ── */
table{width:100%;border-collapse:collapse;background:#fff;border-radius:10px;overflow:hidden;box-shadow:0 1px 6px rgba(0,0,0,.09)}
th{background:#166534;color:#f0fdf4;padding:10px 14px;text-align:left;font-size:.82rem;font-weight:600;letter-spacing:.03em}
td{padding:9px 14px;border-bottom:1px solid #f1f5f9;font-size:.88rem;vertical-align:middle}
tr:last-child td{border-bottom:none}
tr.dragging{opacity:.4}
tr.drag-over td{border-top:3px solid #16a34a}
/* drag handle */
td.drag-handle{cursor:grab;color:#94a3b8;font-size:1.1rem;padding:9px 8px;user-select:none}
td.drag-handle:active{cursor:grabbing}
.wa-link{color:#16a34a;font-weight:600;text-decoration:none}
.wa-link:hover{text-decoration:underline}
.sem-tel{color:#94a3b8;font-style:italic}
.turma-badge{display:inline-block;font-size:.74rem;padding:2px 8px;border-radius:999px;font-weight:600}
.turma-badge.crianca{background:#dbeafe;color:#1d4ed8}
.turma-badge.jovem{background:#dcfce7;color:#15803d}
.recitativo-cell{font-weight:600;color:#7c3aed}
.rodape{margin-top:18px;font-size:.75rem;color:#94a3b8;text-align:center}
</style>
</head>
<body>
<h1>📋 ${titulo}</h1>

<!-- Painel de controle interativo -->
<div class="ctrl">
  <div class="ctrl-group">
    <label for="ctrl-auxiliar">Auxiliar</label>
    <input id="ctrl-auxiliar" value="${nomeAuxiliar}" />
  </div>
  <div class="ctrl-group">
    <label for="ctrl-localidade">Localidade</label>
    <input id="ctrl-localidade" value="${localidade}" list="loc-list" />
    <datalist id="loc-list"><option value="Lourenção"/><option value="Central"/></datalist>
  </div>
  <div class="ctrl-group">
    <label for="ctrl-livro">Livro (bíblico)</label>
    <input id="ctrl-livro" placeholder="Ex: Romanos" list="livros-list" />
    <datalist id="livros-list">
      <option value="Gênesis"/><option value="Êxodo"/><option value="Levítico"/><option value="Números"/>
      <option value="Deuteronômio"/><option value="Josué"/><option value="Juízes"/><option value="Rute"/>
      <option value="1 Samuel"/><option value="2 Samuel"/><option value="1 Reis"/><option value="2 Reis"/>
      <option value="1 Crônicas"/><option value="2 Crônicas"/><option value="Esdras"/><option value="Neemias"/>
      <option value="Ester"/><option value="Jó"/><option value="Salmos"/><option value="Provérbios"/>
      <option value="Eclesiastes"/><option value="Cantares"/><option value="Isaías"/><option value="Jeremias"/>
      <option value="Lamentações"/><option value="Ezequiel"/><option value="Daniel"/><option value="Oséias"/>
      <option value="Joel"/><option value="Amós"/><option value="Obadias"/><option value="Jonas"/>
      <option value="Miquéias"/><option value="Naum"/><option value="Habacuque"/><option value="Sofonias"/>
      <option value="Ageu"/><option value="Zacarias"/><option value="Malaquias"/>
      <option value="Mateus"/><option value="Marcos"/><option value="Lucas"/><option value="João"/>
      <option value="Atos"/><option value="Romanos"/><option value="1 Coríntios"/><option value="2 Coríntios"/>
      <option value="Gálatas"/><option value="Efésios"/><option value="Filipenses"/><option value="Colossenses"/>
      <option value="1 Tessalonicenses"/><option value="2 Tessalonicenses"/><option value="1 Timóteo"/>
      <option value="2 Timóteo"/><option value="Tito"/><option value="Filemon"/><option value="Hebreus"/>
      <option value="Tiago"/><option value="1 Pedro"/><option value="2 Pedro"/><option value="1 João"/>
      <option value="2 João"/><option value="3 João"/><option value="Judas"/><option value="Apocalipse"/>
    </datalist>
  </div>
  <div class="ctrl-group">
    <label for="ctrl-capitulo">Capítulo</label>
    <input id="ctrl-capitulo" type="number" min="1" style="width:80px" placeholder="Cap." />
  </div>
  <div class="ctrl-group">
    <label for="ctrl-verso">Verso inicial</label>
    <input id="ctrl-verso" type="number" min="1" style="width:80px" placeholder="Verso" />
  </div>
  <button class="btn-buscar-biblia" onclick="buscarVerso()">🔍 Buscar versículo</button>
  <div class="biblia-resultado" id="biblia-resultado"></div>
</div>

<p class="aviso">
  ✋ <strong>Arraste as linhas</strong> para reordenar. O versículo é atualizado automaticamente. 
  Clique no telefone para abrir o WhatsApp.
</p>

<table>
  <thead>
    <tr>
      <th title="Arraste para reordenar">⠿</th>
      <th>#</th>
      <th>Nome</th>
      <th>Idade</th>
      <th>Turma</th>
      <th>Recitativo</th>
      <th>Telefone (clique → WhatsApp)</th>
      <th>Responsável</th>
    </tr>
  </thead>
  <tbody id="lista-corpo">
${rows}
  </tbody>
</table>
<p class="rodape">Gerado em ${new Date().toLocaleString('pt-BR')} — Sistema de Reuniões</p>

<script>
(function(){
  /* ── Dados iniciais embutidos ── */
  var AUXILIAR   = ${JSON.stringify(nomeAuxiliar)};
  var LOCALIDADE = ${JSON.stringify(localidade)};
  var REC_PREFIX = ${JSON.stringify(recitativoParsed ? recitativoParsed.prefix : recitativo)};
  var REC_VERSO  = ${recitativoParsed ? recitativoParsed.startVerse : 1};
  var SEQUENCIA  = ${JSON.stringify(sequencia || [])};
  var TEM_SEQUENCIA = SEQUENCIA && SEQUENCIA.length > 0;
  var LIVRO_FIXO  = ${JSON.stringify(livroCap.livro || '')};

  /* ── Preenche controles com valores iniciais ── */
  var parts = REC_PREFIX.match(/^(.*?)\\s+(\\d+)$/);
  document.getElementById('ctrl-livro').value    = parts ? parts[1] : (LIVRO_FIXO || REC_PREFIX);
  document.getElementById('ctrl-capitulo').value = parts ? parts[2] : '';
  document.getElementById('ctrl-verso').value    = REC_VERSO;

  /* ── Utilitários ── */
  function recitivoAtual(verso){
    var livro = (document.getElementById('ctrl-livro').value || '').trim();
    var cap   = (document.getElementById('ctrl-capitulo').value || '').trim();
    return livro + (cap ? ' ' + cap : '') + ':' + verso;
  }
  function refDeSequencia(idx){
    /* Quando ha sequencia (carry-over) usa-a; senao incrementa simples */
    if(TEM_SEQUENCIA && SEQUENCIA[idx]){
      var s = SEQUENCIA[idx];
      var livro = (document.getElementById('ctrl-livro').value || LIVRO_FIXO || '').trim();
      return livro + ' ' + s.capitulo + ':' + s.verso;
    }
    var versoBase = parseInt(document.getElementById('ctrl-verso').value) || REC_VERSO;
    return recitivoAtual(versoBase + idx);
  }

  function msgJovem(nome,genero,refStr){
    var trat = (genero==='irma') ? 'irmã' : 'irmão';
    var primeiro = nome.split(' ')[0];
    var local = document.getElementById('ctrl-localidade').value || LOCALIDADE;
    return 'A paz de Deus '+trat+' '+primeiro+', tudo bem?\\n\\nEstou enviando o seu recitativo para o próximo domingo na reunião de jovens na '+local+'.\\n\\nO recitativo vai ser *'+refStr+'*\\n\\nDeus abençoe e contamos com a vossa presença!';
  }

  function msgCrianca(nomeCrianca,nomeResp,refStr){
    var aux   = document.getElementById('ctrl-auxiliar').value || AUXILIAR;
    var local = document.getElementById('ctrl-localidade').value || LOCALIDADE;
    var primeiroResp    = (nomeResp||'responsável').split(' ')[0];
    var primeiroCrianca = nomeCrianca.split(' ')[0];
    return 'A paz de Deus irmão(a) '+primeiroResp+', tudo bem?\\n\\nMeu nome é '+aux+', auxiliar de jovens e menores e estarei enviando o recitativo do(a) '+primeiroCrianca+' para recitar no próximo domingo na reunião de jovens na '+local+'.\\n\\nO recitativo vai ser *'+refStr+'*\\n\\nDeus abençoe e contamos com a vossa presença!';
  }

  /* ── Renumerar e remontar links ── */
  function renumerar(){
    var tbody = document.getElementById('lista-corpo');
    var rows  = Array.from(tbody.querySelectorAll('tr'));
    var versoBase = parseInt(document.getElementById('ctrl-verso').value) || REC_VERSO;
    var jovenIdx  = 0;

    rows.forEach(function(tr,i){
      /* número da linha */
      tr.querySelector('td.num-cell').textContent = i + 1;

      var ehJovem = tr.dataset.jovem === '1';
      /* data-sequential="1" → verso por pessoa (2a/3a turma)
         data-sequential="0" → mesmo verso para todos (1a turma) */
      var ehSequencial = tr.dataset.sequential === '1';
      var refStr;
      if(ehSequencial){
        /* 2a e 3a turma: verso sequencial, um por membro */
        refStr = refDeSequencia(jovenIdx);
        jovenIdx++;
      } else {
        /* 1a turma: todos recebem o mesmo verso */
        if(TEM_SEQUENCIA){
          refStr = refDeSequencia(0);
        } else {
          refStr = recitivoAtual(versoBase);
        }
      }

      /* Célula recitativo */
      tr.querySelector('td.recitativo-cell').textContent = refStr;

      /* Link WhatsApp */
      var tel    = tr.dataset.tel;
      var telWA  = tr.dataset.telwa;
      var nome   = tr.dataset.nome;
      var genero = tr.dataset.genero;
      var resp   = tr.dataset.resp || '';
      var tdTel  = tr.querySelector('td.tel-cell');

      /* Mensagem: só jovens (>=12) recebem diretamente; crianças (1a/2a turma) → responsável */
      var msg = ehJovem ? msgJovem(nome,genero,refStr) : msgCrianca(nome,resp,refStr);

      if(telWA){
        tdTel.innerHTML = '<a href="https://wa.me/'+telWA+'?text='+encodeURIComponent(msg)+'" class="wa-link" target="_blank" rel="noopener">📱 '+tel+'</a>';
      } else {
        tdTel.innerHTML = '<span class="sem-tel">'+(tel||'—')+'</span>';
      }
    });
  }

  /* ── Drag & Drop (mouse + touch) ── */
  var dragged = null;
  var tbody = document.getElementById('lista-corpo');

  /* ── MOUSE (desktop) ── */
  tbody.addEventListener('dragstart',function(e){
    dragged = e.target.closest('tr');
    if(!dragged) return;
    dragged.classList.add('dragging');
    e.dataTransfer.effectAllowed='move';
  });
  tbody.addEventListener('dragend',function(){
    if(dragged) dragged.classList.remove('dragging');
    document.querySelectorAll('tr.drag-over').forEach(function(r){r.classList.remove('drag-over');});
    dragged = null;
    renumerar();
  });
  tbody.addEventListener('dragover',function(e){
    e.preventDefault();
    var target = e.target.closest('tr');
    if(!target || target===dragged) return;
    document.querySelectorAll('tr.drag-over').forEach(function(r){r.classList.remove('drag-over');});
    target.classList.add('drag-over');
  });
  tbody.addEventListener('drop',function(e){
    e.preventDefault();
    var target = e.target.closest('tr');
    if(!target || target===dragged) return;
    target.classList.remove('drag-over');
    moverLinha(dragged, target);
  });

  /* ── TOUCH (mobile) ── */
  var touchDragged = null;
  var touchClone   = null;
  var touchOffX    = 0;
  var touchOffY    = 0;

  function criarClone(row, touchX, touchY) {
    var rect = row.getBoundingClientRect();
    touchOffX = touchX - rect.left;
    touchOffY = touchY - rect.top;
    var clone = row.cloneNode(true);
    clone.style.cssText = [
      'position:fixed',
      'left:'  + rect.left + 'px',
      'top:'   + rect.top  + 'px',
      'width:' + rect.width + 'px',
      'opacity:0.85',
      'background:#e6f4ea',
      'box-shadow:0 6px 20px rgba(0,0,0,.25)',
      'pointer-events:none',
      'z-index:9999',
      'border-radius:6px',
      'transition:none',
      'display:table',
    ].join(';');
    document.body.appendChild(clone);
    return clone;
  }

  function linhaEmPonto(x, y) {
    var el = document.elementFromPoint(x, y);
    if(!el) return null;
    return el.closest('tr');
  }

  tbody.addEventListener('touchstart', function(e){
    var handle = e.target.closest('.drag-handle');
    if(!handle) return;
    var tr = handle.closest('tr');
    if(!tr) return;
    touchDragged = tr;
    var touch = e.touches[0];
    touchClone = criarClone(tr, touch.clientX, touch.clientY);
    tr.style.opacity = '0.3';
    e.preventDefault();
  }, {passive: false});

  tbody.addEventListener('touchmove', function(e){
    if(!touchDragged || !touchClone) return;
    var touch = e.touches[0];
    touchClone.style.left = (touch.clientX - touchOffX) + 'px';
    touchClone.style.top  = (touch.clientY - touchOffY) + 'px';

    // Highlight target row
    touchClone.style.display = 'none';
    var under = linhaEmPonto(touch.clientX, touch.clientY);
    touchClone.style.display = 'table';

    document.querySelectorAll('tr.drag-over').forEach(function(r){r.classList.remove('drag-over');});
    if(under && under !== touchDragged && tbody.contains(under)){
      under.classList.add('drag-over');
    }
    e.preventDefault();
  }, {passive: false});

  tbody.addEventListener('touchend', function(e){
    if(!touchDragged) return;
    var touch = e.changedTouches[0];

    // Remove clone first so elementFromPoint finds the real rows
    if(touchClone){ document.body.removeChild(touchClone); touchClone = null; }
    touchDragged.style.opacity = '';

    var target = linhaEmPonto(touch.clientX, touch.clientY);
    document.querySelectorAll('tr.drag-over').forEach(function(r){r.classList.remove('drag-over');});

    if(target && target !== touchDragged && tbody.contains(target)){
      moverLinha(touchDragged, target);
    }
    touchDragged = null;
    renumerar();
  }, {passive: false});

  function moverLinha(from, to) {
    var rows  = Array.from(tbody.querySelectorAll('tr'));
    var fromI = rows.indexOf(from);
    var toI   = rows.indexOf(to);
    if(fromI < toI) tbody.insertBefore(from, to.nextSibling);
    else            tbody.insertBefore(from, to);
  }

  /* ── Inputs do painel atualizam em tempo real ── */
  ['ctrl-auxiliar','ctrl-localidade','ctrl-livro','ctrl-capitulo','ctrl-verso'].forEach(function(id){
    document.getElementById(id).addEventListener('input', renumerar);
  });

  /* ── Busca na API da Bíblia Sagrada (bible-api.com) ── */
  window.buscarVerso = function(){
    var livro = document.getElementById('ctrl-livro').value.trim();
    var cap   = document.getElementById('ctrl-capitulo').value.trim();
    var verso = document.getElementById('ctrl-verso').value.trim();
    var el    = document.getElementById('biblia-resultado');
    if(!livro||!cap||!verso){ el.textContent='Preencha livro, capítulo e versículo.'; return; }

    var mapa = {
      'Gênesis':'genesis','Êxodo':'exodus','Levítico':'leviticus','Números':'numbers',
      'Deuteronômio':'deuteronomy','Josué':'joshua','Juízes':'judges','Rute':'ruth',
      '1 Samuel':'1+samuel','2 Samuel':'2+samuel','1 Reis':'1+kings','2 Reis':'2+kings',
      '1 Crônicas':'1+chronicles','2 Crônicas':'2+chronicles','Esdras':'ezra',
      'Neemias':'nehemiah','Ester':'esther','Jó':'job','Salmos':'psalms',
      'Provérbios':'proverbs','Eclesiastes':'ecclesiastes','Cantares':'song+of+solomon',
      'Isaías':'isaiah','Jeremias':'jeremiah','Lamentações':'lamentations','Ezequiel':'ezekiel',
      'Daniel':'daniel','Oséias':'hosea','Joel':'joel','Amós':'amos','Obadias':'obadiah',
      'Jonas':'jonah','Miquéias':'micah','Naum':'nahum','Habacuque':'habakkuk',
      'Sofonias':'zephaniah','Ageu':'haggai','Zacarias':'zechariah','Malaquias':'malachi',
      'Mateus':'matthew','Marcos':'mark','Lucas':'luke','João':'john','Atos':'acts',
      'Romanos':'romans','1 Coríntios':'1+corinthians','2 Coríntios':'2+corinthians',
      'Gálatas':'galatians','Efésios':'ephesians','Filipenses':'philippians',
      'Colossenses':'colossians','1 Tessalonicenses':'1+thessalonians','2 Tessalonicenses':'2+thessalonians',
      '1 Timóteo':'1+timothy','2 Timóteo':'2+timothy','Tito':'titus','Filemon':'philemon',
      'Hebreus':'hebrews','Tiago':'james','1 Pedro':'1+peter','2 Pedro':'2+peter',
      '1 João':'1+john','2 João':'2+john','3 João':'3+john','Judas':'jude','Apocalipse':'revelation'
    };
    var slug = mapa[livro] || livro.toLowerCase().replace(/\\s+/g,'+');
    var url  = 'https://bible-api.com/'+slug+'+'+cap+':'+verso+'?translation=almeida';
    el.textContent = '🔄 Buscando…';
    fetch(url)
      .then(function(r){ return r.json(); })
      .then(function(d){
        if(d.text){
          el.innerHTML = '<strong>'+livro+' '+cap+':'+verso+'</strong> — '+d.text.trim();
        } else {
          el.textContent = '❌ Versículo não encontrado.';
        }
      })
      .catch(function(){ el.textContent = '❌ Erro ao buscar. Verifique a conexão.'; });
  };

  /* ── Inicialização ── */
  renumerar();
})();
</script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitizarNomeArquivo(titulo)}_whatsapp.html`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

